import { ReactNode } from "react";

// Shared device frame for every app mockup on the marketing site.
// The screen renders the real app's dark theme, so the frame stays dark
// while the page around it is light. Pass `image` (a real app screenshot)
// to fill the screen with it instead of a hand-built mock.
export default function PhoneFrame({
  children,
  caption,
  image,
  imageAlt,
}: {
  children?: ReactNode;
  caption?: string;
  image?: string;
  imageAlt?: string;
}) {
  return (
    <div className="w-full flex flex-col items-center" style={{ maxWidth: 330 }}>
      <div
        className="w-full overflow-hidden flex flex-col"
        style={{
          borderRadius: 42,
          border: "1px solid #2a3036",
          background: "#0b0d0f",
          boxShadow:
            "0 0 0 6px #131619, 0 30px 70px rgba(15,23,42,0.3)",
        }}
      >
        {/* Status area */}
        <div className="flex justify-center items-end pb-1.5" style={{ height: 26 }}>
          <div
            style={{
              width: 72,
              height: 6,
              borderRadius: 100,
              background: "#1a1e22",
            }}
          />
        </div>

        {image ? (
          <img
            src={image}
            alt={imageAlt ?? caption ?? "App screenshot"}
            className="w-full block"
            style={{ minHeight: 520, objectFit: "cover" }}
          />
        ) : (
          <div style={{ padding: "12px 12px 16px", minHeight: 520 }}>{children}</div>
        )}

        {/* Home indicator */}
        <div className="flex justify-center items-center" style={{ height: 20 }}>
          <div
            style={{
              width: 96,
              height: 4,
              borderRadius: 100,
              background: "#1a1e22",
            }}
          />
        </div>
      </div>

      {caption && (
        <div className="mk-label mt-5 text-center" style={{ fontSize: 10 }}>
          {caption}
        </div>
      )}
    </div>
  );
}

// Shared screen primitives so every mockup reads as one app.
export const screen = {
  card: {
    background: "#14171a",
    border: "1px solid #22272b",
    borderRadius: 12,
    padding: 12,
  } as const,
  label: {
    fontFamily: '"Inter", sans-serif',
    fontWeight: 500,
    fontSize: 8,
    letterSpacing: "0.12em",
    textTransform: "uppercase",
    color: "#8b959c",
  } as const,
  mono: {
    fontFamily: '"Inter", sans-serif',
    fontWeight: 500,
    fontFeatureSettings: '"tnum"',
  } as const,
  ink: "#edf1f2",
  muted: "#8b959c",
  dim: "#5d666d",
  line: "#22272b",
  signal: "#00ff84",
  amber: "#ffb224",
  red: "#ff5c5c",
};
